import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useCreatePosition, useSetPositionActive, useUpdatePosition } from '@/hooks/catalog-admin';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import type { PositionView } from '@shared/schemas/position';

/** Create or edit a job position. `position` absent means create. */
export function PositionFormDialog({
  open,
  onOpenChange,
  position,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position?: PositionView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <PositionForm
            position={position}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PositionForm({ position, onDone }: { position?: PositionView; onDone: () => void }) {
  const toast = useToast();
  const isEdit = position !== undefined;

  const [name, setName] = useState(position?.name ?? '');
  const [isHiring, setIsHiring] = useState(position?.isHiring ?? false);

  const create = useCreatePosition();
  const update = useUpdatePosition(position?.id ?? '');
  const setActive = useSetPositionActive(position?.id ?? '');
  const mutation = isEdit ? update : create;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    mutation.mutate(
      { name, isHiring },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? `Saved ${saved.name}.` : `Added ${saved.name}.`);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${position.name}` : 'Add a position'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        A job title someone can apply for on the careers page.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Title"
          name="name"
          required
          value={name}
          error={fieldErrors.name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          placeholder="Lead Cleaner"
        />

        <div className="rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="isHiring"
              checked={isHiring}
              onChange={(event) => {
                setIsHiring(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-label text-ink-900">Advertise this on the careers page</span>
          </label>
          <p className="mt-1 text-meta text-ink-500">
            Ticked, it appears in the "Position applied for" list on the public form and people can
            apply for it.
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          {isEdit ? (
            <Button
              variant="outline"
              className="sm:mr-auto"
              disabled={setActive.isPending}
              onClick={() => {
                setActive.mutate(!position.isActive, {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive
                        ? `${saved.name} is active again.`
                        : // Retiring also stops it being advertised, and saying
                          // so is the difference between a toast and a surprise.
                          `${saved.name} retired and taken off the careers page.`,
                    );
                    onDone();
                  },
                  onError: (cause) => {
                    toast.error(
                      cause instanceof ApiError ? cause.message : 'That could not be saved.',
                    );
                  },
                });
              }}
            >
              {position.isActive ? 'Retire this position' : 'Bring it back'}
            </Button>
          ) : null}

          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add position'}
          </Button>
        </div>
      </form>
    </>
  );
}
